import React, { useId } from "react";
import { AlertCircle, Check, ChevronDown, CloudOff, Loader2 } from "lucide-react";

import { Switch } from "@/components/ui/switch";
import { slug } from "@/lib/format";
import { cn } from "@/lib/utils";

const cx = cn;

const VARIANTS = {
  primary: "bg-primary text-white hover:bg-[#5c1f2a] shadow-sm",
  outline: "border border-primary text-primary bg-white/60 hover:bg-[#F6E3E4]",
  ghost: "text-primary hover:bg-[#F4EBE6]",
  danger: "bg-[#9E3C3E] text-white hover:bg-[#873234]",
  rose: "bg-[#F6E3E4] text-primary hover:bg-[#efd3d5]",
};

export function Button({ title, onClick, variant = "primary", size = "md", loading, disabled, icon: Icon, testId, className, type = "button", children }) {
  return (
    <button
      type={type}
      data-testid={testId}
      onClick={onClick}
      disabled={disabled || loading}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-full font-bold transition-all duration-200 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none whitespace-nowrap",
        size === "sm" ? "h-10 px-4 text-[13px]" : "h-12 px-6 text-[15px]",
        VARIANTS[variant], className,
      )}
    >
      {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : Icon ? <Icon className={size === "sm" ? "h-4 w-4" : "h-[18px] w-[18px]"} /> : null}
      {!loading || size !== "sm" ? title ?? children : null}
    </button>
  );
}

const inputCls = "w-full rounded-xl border bg-white px-4 text-[15px] text-foreground placeholder:text-[#756E69] transition-colors focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/15";

export function Field({ label, error, hint, testId, multiline, className, ...rest }) {
  const id = useId();
  const El = multiline ? "textarea" : "input";
  return (
    <div className={cx("mb-4", className)}>
      <label htmlFor={id} className="mb-1.5 block text-sm font-bold text-foreground">{label}</label>
      <El id={id} data-testid={testId} {...rest} className={cx(inputCls, multiline ? "min-h-[110px] py-3 resize-y" : "h-12", error ? "border-[#9E3C3E]" : "border-border")} />
      {error ? <p className="mt-1 text-xs font-medium text-[#9E3C3E]" data-testid={`${testId}-error`}>{error}</p> : hint ? <p className="mt-1 text-xs text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function SelectField({ label, value, placeholder, onClick, error, testId }) {
  return (
    <div className="mb-4">
      <span className="mb-1.5 block text-sm font-bold">{label}</span>
      <button type="button" data-testid={testId} onClick={onClick} className={cx(inputCls, "flex h-12 items-center justify-between text-left", error ? "border-[#9E3C3E]" : "border-border")}>
        <span className={cx("truncate", !value && "text-muted-foreground")}>{value || placeholder}</span>
        <ChevronDown className="h-4 w-4 text-muted-foreground" />
      </button>
      {error ? <p className="mt-1 text-xs font-medium text-[#9E3C3E]">{error}</p> : null}
    </div>
  );
}

export function Chip({ label, selected, onClick, testId, icon: Icon }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} aria-pressed={!!selected}
      className={cx("inline-flex h-10 shrink-0 items-center gap-1.5 rounded-full border px-4 text-sm font-medium transition-colors",
        selected ? "border-primary bg-primary text-white" : "border-border bg-white text-foreground hover:border-[#D48D93] hover:bg-[#F6E3E4]")}>
      {Icon ? <Icon className="h-3.5 w-3.5" /> : null}{label}
    </button>
  );
}

export function ChipGroup({ options, value, onChange, multi = true, noPreference = false, testIdPrefix, labels }) {
  const toggle = (o) => {
    if (!multi) return onChange(value[0] === o ? [] : [o]);
    onChange(value.includes(o) ? value.filter((v) => v !== o) : [...value, o]);
  };
  return (
    <div className="flex flex-wrap gap-2">
      {noPreference ? <Chip testId={`${testIdPrefix}-no-preference`} label="No preference" selected={value.length === 0} onClick={() => onChange([])} /> : null}
      {options.map((o) => <Chip key={o} testId={`${testIdPrefix}-${slug(o)}`} label={labels?.[o] ?? o} selected={value.includes(o)} onClick={() => toggle(o)} />)}
    </div>
  );
}

export function Checkbox({ checked, onToggle, children, testId, error }) {
  const id = useId();
  // Box is the focusable checkbox; the label is a separate element so it can contain links (no <a> inside <button>).
  return (
    <div className="mb-3 flex w-full items-start gap-3 text-left">
      <button type="button" role="checkbox" aria-checked={checked} aria-labelledby={id} data-testid={testId} onClick={onToggle}
        className={cx("mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2", checked ? "border-primary bg-primary" : error ? "border-[#9E3C3E]" : "border-[#C8BDB2] bg-white")}>
        {checked ? <Check className="h-4 w-4 text-white" /> : null}
      </button>
      <span id={id} onClick={onToggle} className={cx("cursor-pointer select-none text-sm leading-5", error && "text-[#9E3C3E]")}>{children}</span>
    </div>
  );
}

export function Toggle({ checked, onChange, testId, disabled, label }) {
  return <Switch data-testid={testId} aria-label={label} checked={!!checked} disabled={disabled} onCheckedChange={onChange} className="h-7 w-12 [&>span]:h-6 [&>span]:w-6 [&>span]:data-[state=checked]:translate-x-5" />;
}

export function SectionLabel({ children, note }) {
  return (
    <div className="mb-2.5 mt-1 flex items-baseline gap-2">
      <span className="text-[15px] font-bold">{children}</span>
      {note ? <span className="text-xs text-muted-foreground">{note}</span> : null}
    </div>
  );
}

const TONES = {
  neutral: "bg-[#F4EBE6] text-foreground",
  rose: "bg-[#F6E3E4] text-primary",
  brand: "bg-primary text-white",
  gold: "bg-[#C19B53] text-white",
  success: "bg-[#3A6C54] text-white",
  error: "bg-[#9E3C3E] text-white",
};

export function Pill({ label, tone = "neutral", icon: Icon, testId, className }) {
  return (
    <span data-testid={testId} className={cx("inline-flex max-w-full items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-bold", TONES[tone], className)}>
      {Icon ? <Icon className="h-3 w-3 shrink-0" /> : null}<span className="truncate">{label}</span>
    </span>
  );
}

export function PageHeader({ title, subtitle, right, testId }) {
  return (
    <div className="mb-6 flex items-start gap-4" data-testid={testId}>
      <div className="min-w-0 flex-1">
        <h1 className="text-[34px] leading-[1.1] text-foreground md:text-[42px]">{title}</h1>
        {subtitle ? <p className="mt-1 text-sm text-muted-foreground" data-testid="page-subtitle">{subtitle}</p> : null}
      </div>
      {right}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, text, action, testId }) {
  return (
    <div className="mx-auto flex max-w-md flex-col items-center px-6 py-16 text-center fade-up" data-testid={testId}>
      <div className="mb-4 flex h-[72px] w-[72px] items-center justify-center rounded-full bg-[#F6E3E4]"><Icon className="h-8 w-8 text-primary" /></div>
      <h2 className="text-2xl">{title}</h2>
      {text ? <p className="mt-2 text-sm leading-6 text-muted-foreground">{text}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({ message, onRetry, testId }) {
  return <EmptyState testId={testId} icon={CloudOff} title="Something went wrong" text={message} action={<Button testId={`${testId}-retry`} title="Retry" variant="outline" onClick={onRetry} className="mt-5" />} />;
}

export function Loading({ testId = "loading" }) {
  return <div className="flex flex-1 items-center justify-center p-16" data-testid={testId}><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
}

export function ErrorBox({ children, testId }) {
  return (
    <div data-testid={testId} className="mb-4 flex items-center gap-2 rounded-xl bg-[#F6E3E4] p-3 text-sm font-medium text-[#9E3C3E]">
      <AlertCircle className="h-[18px] w-[18px] shrink-0" />{children}
    </div>
  );
}

export const TestModeBanner = ({ testId }) => (
  <p data-testid={testId} className="rounded-xl border border-dashed border-[#C19B53] bg-[#C19B53]/10 px-4 py-2.5 text-center text-xs font-bold tracking-wide text-[#8a6c33]">TEST MODE — no real money is charged</p>
);

export { cx };
